import type { Object3D, Scene } from 'three';

/**
 * Simplify material for AR export
 */
export const simplifyMaterialForAR = (mat: unknown): void => {
	if (!mat) return;

	const material = mat as Record<string, unknown>;

	// Keep diffuse map, remove expensive secondary maps
	material.normalMap = null;
	material.roughnessMap = null;
	material.metalnessMap = null;
	material.aoMap = null;
	material.displacementMap = null;
	material.bumpMap = null;

	// Reduce emissive intensity
	if (typeof material.emissiveIntensity === 'number' && material.emissiveIntensity > 1) {
		material.emissiveIntensity = 1;
	}

	material.flatShading = false;
	material.needsUpdate = true;
};

/**
 * Optimize scene for AR export
 */
export const optimizeSceneForAR = (scene: Scene): void => {
	const objectsToRemove: Object3D[] = [];

	scene.traverse((object: unknown) => {
		const obj = object as Record<string, unknown>;

		// Disable shadows
		if (obj.castShadow !== undefined) {
			obj.castShadow = false;
		}
		if (obj.receiveShadow !== undefined) {
			obj.receiveShadow = false;
		}

		// Simplify materials
		if (obj.material) {
			if (Array.isArray(obj.material)) {
				obj.material.forEach((mat: unknown) => simplifyMaterialForAR(mat));
			} else {
				simplifyMaterialForAR(obj.material);
			}
		}

		// Simplify geometry
		const typedObj = obj as { type?: string; geometry?: { attributes: { position?: { count: number }; tangent?: unknown; color?: unknown }; deleteAttribute: (name: string) => void } };
		if (typedObj.type === 'Mesh' && typedObj.geometry) {
			const { geometry } = typedObj;

			// Remove expensive attributes
			if (geometry.attributes.tangent) {
				geometry.deleteAttribute('tangent');
			}
			if (geometry.attributes.color) {
				geometry.deleteAttribute('color');
			}

			// Skip tiny meshes
			if (geometry.attributes.position && geometry.attributes.position.count < 3) {
				objectsToRemove.push(object as Object3D);
			}
		}

		// Remove helpers, lights, cameras
		if (['Helper', 'Light', 'Camera', 'Line', 'Points'].includes(typedObj.type as string)) {
			objectsToRemove.push(object as Object3D);
		}
	});

	// Remove identified objects
	objectsToRemove.forEach((obj) => {
		if (obj.parent) {
			obj.parent.remove(obj);
		}
	});

	scene.updateMatrixWorld(true);
};

/**
 * Dispose of a temporary scene
 */
export const disposeScene = (scene: Scene): void => {
	scene.traverse((object: unknown) => {
		const obj = object as { geometry?: { dispose: () => void }; material?: { dispose: () => void } | Array<{ dispose: () => void }> };
		if (obj.geometry) {
			obj.geometry.dispose();
		}
		if (obj.material) {
			if (Array.isArray(obj.material)) {
				obj.material.forEach((m) => m.dispose());
			} else {
				obj.material.dispose();
			}
		}
	});
	scene.clear();
};

/**
 * Trigger file download
 */
export const triggerDownload = (
	data: ArrayBuffer | object,
	filename: string,
	format: 'glb' | 'gltf'
): void => {
	let blob: Blob;
	let finalFilename = filename;

	if (data instanceof ArrayBuffer) {
		blob = new Blob([data], { type: 'application/octet-stream' });
	} else {
		const output = JSON.stringify(data, null, 2);
		blob = new Blob([output], { type: 'text/plain' });
		finalFilename = filename.replace('.glb', '.gltf');
	}

	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = finalFilename;
	link.click();
	URL.revokeObjectURL(url);
};
